import { useCallback, useRef } from 'react'
import { BlockButton, Breakpoints, ConfirmationModal, Icon, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn } from '../../../components';
import { usePageActions } from '../../PageContent';
import { MenuItem, MenuSelectEvent } from '@progress/kendo-react-layout';
import { BlogAuthorModel, ODataResponse } from '../../../data/entities';
import { GridCellProps } from '@progress/kendo-react-grid';
import { useSharedState } from '../../../app/sharedProperty';
import { showSuccessMessage } from '../../../app/tools';
import { BlogService } from '../../../services';
import { AddAuthorModal } from '.';
import EditAuthorModal from './components/EditAuthorModal';

export function BlogAuthorsList() {
  
  const gridData = useSharedState<Array<BlogAuthorModel>>([]);
  const gridRef = useRef<ODataGrid>(null);
  const showAddState = useSharedState(false);
  const showEditState = useSharedState(false);
  const showDeleteState = useSharedState(false);
  const editItemState = useSharedState<BlogAuthorModel>({} as BlogAuthorModel)
  const deleteItemState = useSharedState<BlogAuthorModel>({} as BlogAuthorModel)

  const blogService = new BlogService();

  usePageActions(
    <BlockButton onClick={() => { showAddState.setter(true); }}>
      <IconAndText iconName={IconDefinitions.add} text="Add New Author" />
    </BlockButton>
  );

  const getAuthors = (oDataQuery: string) => 
     blogService.getBlogAuthorsAsync(oDataQuery);

  const onRowMenuSelect = (e: MenuSelectEvent, dataItem: any) => {
    if (dataItem.isDeleted) return;
    switch (e.item.data.action){
      case "edit":
        showEdit(dataItem);
        break;
      case "delete":
        showDelete(dataItem);
    }
  }

  const showEdit = (dataItem: BlogAuthorModel) => {
    editItemState.setter(dataItem);
    showEditState.setter(true);
  }

  const showDelete = (dataItem: BlogAuthorModel) => {
    deleteItemState.setter(dataItem);
    showDeleteState.setter(true);
  }

  const addAuthor = async (author: BlogAuthorModel) => {
    try {
      showAddState.setter(false);
      await blogService.addBlogAuthorAsync(author);
      gridRef.current?.refreshData();
      showSuccessMessage("Author added successfully.");
    } catch (err) {
      throw new Error(`Unable to add new author, ${err}`);
    }
  };

  const editAuthor = async (author: BlogAuthorModel) => {
    try {
      showEditState.setter(false);
      await blogService.editBlogAuthorAsync(author);
      gridRef.current?.refreshData();
      showSuccessMessage("Author updated successfully.");
    } catch (err) {
      throw new Error(`Unable to update author: ${err}`);
    }
  }

  const deleteAuthor = async (author: BlogAuthorModel) => {
    try {
      await blogService.deleteBlogAuthorByIdAsync(author.id);
      gridRef.current?.refreshData();
      showSuccessMessage("Author deleted successfully.");
    } catch(err) {
      throw new Error(`Unable to delete author: ${err}`);
    }
  };

  const commandsCell = useCallback(
    (props: GridCellProps) => (
      <td className="text-center">
        <Icon
          iconName={IconDefinitions.delete}
          className="text-danger"
          onClick={() => showDelete(props.dataItem)}
        />
      </td>
    ),
    [showDelete]
  );

  return (
    <>
      <ODataGrid
        ref={gridRef}
        fullHeight={true}
        getData={getAuthors as () => Promise<ODataResponse<any>>}
        dataState={gridData}
        onRowMenuSelect={onRowMenuSelect}
        onRowClick={(e) => { showEdit(e.dataItem); }}
        sort={[{ field: "name", dir: "desc"}]}
        scrollable="scrollable"
        pageSize={100}
      >
        <ResponsiveGridColumn
          title="Display Name"
          field="displayName"
          sortable={true}
        />
        <ResponsiveGridColumn
          className="column-url"
          field="url"
          title=" "
          sortable={false}
          filterable={false}
          headerCell={() => null}
          cell={commandsCell}
          width={50}
          breakpoint={Breakpoints.sm}
        />
        <MenuItem
          text="Delete"
          data={{ action: "delete" }}
          icon={IconDefinitions.delete}
        />
      </ODataGrid>
      
      <AddAuthorModal
        showState={showAddState}
        onSubmit={addAuthor}
      />
      <EditAuthorModal
        showState={showEditState}
        dataItemState={editItemState}
        onSubmit={editAuthor}
      />
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.friendlyTitle}`}
        message={`You are about to delete this author, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.friendlyTitle}?`}
        confirmText="Delete"
        onConfirm={() => deleteAuthor(deleteItemState.value)}
      />
    </>
  )
}

export default BlogAuthorsList
